import React, { useEffect, useState, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import ReactDOM from 'react-dom';
import "./Meetingtimer.css";

// FIX 1: Create the portal container SYNCHRONOUSLY at module load time,
// NOT inside a useEffect. On iOS Safari, useEffect fires after paint, so
// portalContainerRef.current is null on the first render, causing the
// component to return null and never recover.
const PORTAL_ID = 'meeting-timer-portal-root';

const getOrCreatePortalContainer = () => {
  // Guard for SSR environments
  if (typeof document === 'undefined') return null;

  let container = document.getElementById(PORTAL_ID);
  if (!container) {
    container = document.createElement('div');
    container.id = PORTAL_ID;

    container.style.cssText = [
      'position: fixed',
      'top: 0',
      'left: 0',
      'width: 0',
      'height: 0',
      'overflow: visible',
      'z-index: 2147483647',
      // FIX 2: DO NOT use transform: translateZ(0) on the portal container.
      // On iOS Safari, any CSS transform on an ancestor creates a new
      // stacking/containing block, which BREAKS position:fixed on descendants.
      // The children lose their viewport-relative positioning and become
      // invisible or misplaced. Remove transform entirely — z-index alone
      // is sufficient for layering above video tiles.
      //
      // Also remove `isolation: isolate` — it's redundant here and can
      // interfere with Safari's compositing in some iOS versions.
      'pointer-events: none',
    ].join('; ');

    document.body.appendChild(container);
  }
  return container;
};

// Create synchronously so it's ready before any render
const portalContainer = getOrCreatePortalContainer();

const MeetingTimer = ({ onMeetingEnd }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [meetingInfo, setMeetingInfo] = useState(null);
  const [timeRemaining, setTimeRemaining] = useState(null);
  const [meetingStatus, setMeetingStatus] = useState('active');
  const [showWarning, setShowWarning] = useState(false);
  const timerRef = useRef(null);
  const warningShownRef = useRef(false);
  const fiveMinWarningShownRef = useRef(false);

  const parseLocalDateTime = (date, time) => {
    const [year, month, day] = date.split('-').map(Number);
    const [hour, minute] = time.split(':').map(Number);
    return new Date(year, month - 1, day, hour, minute, 0, 0);
  };

  useEffect(() => {
    // FIX 3: Always read from window.location.search directly.
    // React Router's `location.search` can be stale or empty on the first
    // render in iOS Safari due to bfcache restoration and hydration timing.
    // window.location.search is always current and synchronous.
    const searchString = window.location.search;
    const urlParams = new URLSearchParams(searchString);
    const fromTime = urlParams.get('fromTime');
    const toTime = urlParams.get('toTime');
    const date = urlParams.get('date');

    console.log('🔍 MeetingTimer - Checking URL parameters:');
    console.log('  fromTime:', fromTime);
    console.log('  toTime:', toTime);
    console.log('  date:', date);

    if (fromTime && toTime && date) {
      const startDateTime = parseLocalDateTime(date, fromTime);
      const endDateTime = parseLocalDateTime(date, toTime);

      setMeetingInfo({
        fromTime,
        toTime,
        date,
        endDateTime,
        startDateTime,
      });

      console.log('✅ Meeting timer initialized');
    } else {
      console.log('⚠️ Missing timing parameters in URL');
    }
    // FIX 4: Depend on location.search so this re-runs if the URL changes,
    // but the actual read uses window.location.search for reliability.
  }, [location.search]);

  useEffect(() => {
    if (!meetingInfo) return;

    console.log('▶️ Starting meeting timer interval...');

    const tick = () => {
      const now = new Date();
      const remaining = meetingInfo.endDateTime - now;

      setTimeRemaining(remaining);

      if (remaining <= 0) {
        console.log('⏱️ Time is up!');
        handleMeetingEnd();
      } else if (remaining <= 5 * 60 * 1000 && !fiveMinWarningShownRef.current) {
        console.log('⚠️ 5 minutes remaining');
        setMeetingStatus('warning');
        setShowWarning(true);
        fiveMinWarningShownRef.current = true;
        setTimeout(() => setShowWarning(false), 10000);
      } else if (remaining <= 60 * 1000 && !warningShownRef.current) {
        console.log('🚨 1 minute remaining');
        setMeetingStatus('ending');
        warningShownRef.current = true;
      }
    };

    tick();
    timerRef.current = setInterval(tick, 1000);

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        console.log('👁️ Tab visible — refreshing timer');
        tick();
      }
    };

    // FIX 5: pagehide/pageshow must be on window, and pageshow needs the
    // persisted check. Additionally, on iOS the interval is KILLED when the
    // app is backgrounded. Re-create it on pageshow so the timer resumes.
    const handlePageShow = (e) => {
      if (e.persisted) {
        console.log('📄 bfcache restore — restarting timer interval');
        // Clear the dead interval and restart it
        if (timerRef.current) clearInterval(timerRef.current);
        tick();
        timerRef.current = setInterval(tick, 1000);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('pageshow', handlePageShow);

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('pageshow', handlePageShow);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meetingInfo]);

  const handleMeetingEnd = () => {
    console.log('🔚 handleMeetingEnd called');
    setMeetingStatus('ended');

    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    if (onMeetingEnd) {
      onMeetingEnd();
    } else {
      alert('Meeting time has ended. You will be redirected shortly.');
      setTimeout(() => navigate('/'), 3000);
    }
  };

  const formatTime12hr = (time24) => {
    if (!time24) return '';
    const [hourStr, minute] = time24.split(':');
    let hour = parseInt(hourStr, 10);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    hour = hour % 12 || 12;
    return `${hour}:${minute} ${ampm}`;
  };

  const formatTimeRemaining = (ms) => {
    if (ms === null || ms === undefined) return 'Loading...';
    if (ms <= 0) return 'Meeting Ended';

    const totalSeconds = Math.floor(ms / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    if (hours > 0) return `${hours}h ${minutes}m remaining`;
    if (minutes > 0) return `${minutes}m ${seconds}s remaining`;
    return `${seconds}s remaining`;
  };

  // FIX 6: Don't gate rendering on portalContainerRef — the container now
  // exists synchronously. Only skip render if there's no meeting info or
  // if (SSR edge-case) the container couldn't be created.
  if (!meetingInfo || !portalContainer) return null;

  console.log('✅ MeetingTimer rendering with status:', meetingStatus);

  const timerContent = (
    <>
      <div className={`meeting-timer ${meetingStatus}`} style={{ pointerEvents: 'auto' }}>
        <div className="timer-icon">
          {/* Inline SVG: reliable on all iOS Safari versions */}
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10 10-4.5 10-10S17.5 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm.5-13H11v6l5.2 3.2.8-1.3-4.5-2.7V7z"/>
          </svg>
        </div>
        <div className="timer-info">
          <div className="timer-schedule">
            {formatTime12hr(meetingInfo.fromTime)} - {formatTime12hr(meetingInfo.toTime)}
          </div>
          <div className="timer-remaining">
            {formatTimeRemaining(timeRemaining)}
          </div>
        </div>
      </div>

      {showWarning && (
        <div className="meeting-warning-modal" style={{ pointerEvents: 'auto' }}>
          <div className="warning-content">
            <div className="warning-icon">⚠️</div>
            <h3>Meeting Ending Soon</h3>
            <p>This meeting will end in 5 minutes.</p>
          </div>
        </div>
      )}

      {meetingStatus === 'ended' && (
        <div className="meeting-ended-overlay" style={{ pointerEvents: 'auto' }}>
          <div className="ended-content">
            <div className="ended-icon">⏰</div>
            <h2>Meeting Time Has Ended</h2>
            <p>Thank you for joining. Redirecting you now...</p>
          </div>
        </div>
      )}
    </>
  );

  return ReactDOM.createPortal(timerContent, portalContainer);
};

export default MeetingTimer;